import sortBy from 'lodash/sortBy';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Logger from 'browser-logging/src/browser/Logger';

import { ILuzmoDashboardDto } from 'common/src/types/luzmo.dtos';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';

import {
  deleteCustomDashboardService,
  getDashboardBootstrap,
} from 'portal/src/api/LuzmoService';
import useAuth from 'portal/src/auth/useAuth';

import LuzmoContext, {
  type LuzmoCredentials,
  type LuzmoDashboardListItem,
  type LuzmoRefreshResult,
} from './LuzmoContext';

function filterDashboards(dashboards: ILuzmoDashboardDto[] = []) {
  return sortBy(
    dashboards
      .filter((dashboard) => dashboard.name?.en)
      .map((dashboard) => ({
        id: dashboard.id,
        name: dashboard.name?.en as string,
      })),
    (d) => d.name.toLowerCase(),
  ) as LuzmoDashboardListItem[];
}

const EMPTY_REFRESH_RESULT: LuzmoRefreshResult = {
  customDashboards: [],
  dashboardIds: {},
  credentials: {},
};

export function LuzmoProvider({ children }: { children: React.ReactNode }) {
  const { featureChecker, getUser, isAuthenticated, isReadyForAuthorization } =
    useAuth();
  const user = getUser?.();
  const [credentials, setCredentials] = useState<LuzmoCredentials>({});
  const [dashboardIds, setDashboardIds] = useState<Record<string, string>>({});
  const [customDashboards, setCustomDashboards] = useState<
    LuzmoDashboardListItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const customDashboardsEnabled = useMemo(() => {
    return (
      featureChecker?.productFeatureEnabled?.(
        ProductFeatures.customDashboards,
      ) ?? false
    );
    // If an org switch occurs, re-check if custom dashboards are enabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureChecker, user?.organizationId]);

  const resetState = useCallback(() => {
    setCredentials({});
    setDashboardIds({});
    setCustomDashboards([]);
    setError(false);
    setIsLoading(false);
  }, []);

  const refreshData = useCallback(async (): Promise<LuzmoRefreshResult> => {
    if (!isAuthenticated?.() || !isReadyForAuthorization?.()) {
      resetState();
      return EMPTY_REFRESH_RESULT;
    }

    setIsLoading(true);
    setError(false);

    try {
      const data = await getDashboardBootstrap();
      const nextViewerCredentials = data.credentials ?? {};
      const nextDashboardIds = data.dashboardIds ?? {};
      const nextCustomDashboards =
        customDashboardsEnabled && data.customDashboards
          ? filterDashboards(data.customDashboards)
          : [];

      setCredentials(nextViewerCredentials);
      setDashboardIds(nextDashboardIds);
      setCustomDashboards(nextCustomDashboards);

      return {
        customDashboards: nextCustomDashboards,
        dashboardIds: nextDashboardIds,
        credentials: nextViewerCredentials,
      };
    } catch (err) {
      Logger.captureError(err as Error);
      setError(true);
      return EMPTY_REFRESH_RESULT;
    } finally {
      setIsLoading(false);
    }
  }, [
    customDashboardsEnabled,
    isAuthenticated,
    isReadyForAuthorization,
    resetState,
  ]);

  useEffect(() => {
    void refreshData();
  }, [refreshData]);

  const removeCustomDashboard = useCallback((dashboardId: string) => {
    setCustomDashboards((currentDashboards) =>
      currentDashboards.filter((dashboard) => dashboard.id !== dashboardId),
    );
  }, []);

  const deleteCustomDashboard = useCallback(
    async (dashboardId: string) => {
      if (!credentials.key || !credentials.token) {
        return false;
      }

      try {
        await deleteCustomDashboardService(
          dashboardId,
          credentials.key,
          credentials.token,
        );

        removeCustomDashboard(dashboardId);
        await refreshData();
        return true;
      } catch (err) {
        Logger.captureError(err as Error);
        return false;
      }
    },
    [credentials.key, credentials.token, refreshData, removeCustomDashboard],
  );

  const value = useMemo(
    () => ({
      customDashboards,
      dashboardIds,
      deleteCustomDashboard,
      error,
      isLoading,
      refreshData,
      credentials,
    }),
    [
      customDashboards,
      dashboardIds,
      deleteCustomDashboard,
      error,
      isLoading,
      refreshData,
      credentials,
    ],
  );

  return (
    <LuzmoContext.Provider value={value}>{children}</LuzmoContext.Provider>
  );
}
